<template>
  <div>
    <h2>{{ $t('upload.photos') }}</h2>

    <v-card class=" mb-10 pa-6" elevation="0" tile>
      <v-row v-if="onRemoveSelection">
        <v-col>
          <div class="d-flex align-center">
            <v-checkbox @click="selectAllPhotos" class="mr-1"/>
            <span class="text-h6 font-weight-light">
              Selecionar todas
            </span>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-for="photo in realtyPhotos"
          :key="photo.id"
          class="d-flex child-flex"
          cols="6" sm="4"
        >
          <v-checkbox v-model="photosToRemove" :value="photo.id" v-if="onRemoveSelection" />

          <v-hover v-slot="{ hover }">
            <v-img
              @click="showPhoto(photo.external_url)"
              :src="photo.external_url"
              contain
              height="120px"
              class="grey lighten-2 "
            >
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular indeterminate color="primary" />
                </v-row>
              </template>

              <v-overlay v-if="!onRemoveSelection"
                :class="{ 'pointer': hover }"
                :value="hover"
                absolute
              >
                <v-icon color="primary" large>
                  mdi-eye
                </v-icon>
              </v-overlay>

            </v-img>
          </v-hover>
        </v-col>

        <v-col
          v-for="(photo, key) in selectedPhotos"
          :key="key"
          class="d-flex child-flex"
          cols="6" sm="4"
        >
          <v-img
            :src="getUrl(key)"
            contain
            height="120px"
            class="grey lighten-2 "
          >
            <template v-slot:placeholder>
              <v-row
                class="fill-height ma-0"
                align="center"
                justify="center"
              >
                <v-progress-circular indeterminate color="primary" />
              </v-row>
            </template>

            <v-overlay v-if="uploading" absolute opacity="0.7">
              <v-row
                class="fill-height ma-0 "
                align="center"
                justify="center"
              >
                <v-progress-circular indeterminate color="primary" />
              </v-row>
              <v-row >
                <span class="font-weight-medium">
                  Enviando
                </span>
              </v-row>
            </v-overlay>
          </v-img>
        </v-col>

        <v-col v-if="!alreadyHasPhotos && !uploading">
          <h5 class="text-h5 text-center">
            {{ $t('realty.label.doesNotHavePhotos') }}
          </h5>
          <p class="text-h6 font-weight-light text-center">
            {{ $t('realty.label.addClickingBelow') }}
          </p>
        </v-col>
      </v-row>

      <v-divider v-if="alreadyHasPhotos" class="my-5" />

      <v-row>
        <v-col v-if="!onRemoveSelection" :class="{'justify-end': alreadyHasPhotos, 'justify-center': !alreadyHasPhotos}" class="d-flex">
          <v-btn
            @click="$refs.photoInputUpload.click()"
            :disabled="uploading"
            icon
            color="primary"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>

          <input
            v-show="false"
            multiple="multiple"
            ref="photoInputUpload"
            type="file"
            @change="updateSelectedPhotos"
          />
        </v-col>

        <v-col v-if="alreadyHasPhotos && !onRemoveSelection" class="d-flex justify-start">
          <v-btn
            icon
            :disabled="uploading"
            color="error"
            @click="handleRemoveSelection"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-col>

        <v-col v-if="onRemoveSelection" class="d-flex justify-end">
          <v-btn
            @click="handleRemoveSelection"
            text
            color="primary">
            <span>
              Cancelar
            </span>
          </v-btn>
        </v-col>
        <v-col v-if="onRemoveSelection" class="d-flex justify-start">
          <v-btn @click="deleteSelectedPhotos" :disabled="photosToRemove.length <= 0" text color="error">
            <span>
              Remover
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <ShowPhotoModal ref="photoModal" />
  </div>
</template>

<script>
import ShowPhotoModal from '@/components/realty/ShowPhotoModal'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'UpdateRealtyPhotos',
  props: {
    realtyId: {
      required: true
    },
    /**
     * A imobiliária dona do imóvel. Usuário de imobiliária carrega a própria e o backend ignora
     * este valor; usuário de prestador de serviço não tem e trabalha em nome de um cliente, então
     * sem ele o backend não encontra o imóvel.
     */
    realEstateId: {
      required: false,
      default: null
    }
  },
  components: {
    ShowPhotoModal
  },
  data() {
    return {
      selectedPhotos: [],
      hover: false,
      onRemoveSelection: false,
      uploading: false,
      photosToRemove: [],
      preparedToUploadPhotos: []
    }
  },
  mounted() {
    this.fetchRealtyPhotos(this.realtyId)
  },
  computed: {
    ...mapGetters(['realtyPhotos']),

    alreadyHasPhotos() {
      return this.realtyPhotos ? this.realtyPhotos.length > 0 : false
    }
  },
  watch: {
    realtyId(newValue) {
      this.onRemoveSelection = false;
      this.resetPhotos()
      this.fetchRealtyPhotos(newValue)
    }
  },
  methods: {
    ...mapActions(['addRealtyPhotos', 'deleteRealtyPhotos', 'fetchRealtyPhotos']),

    getUrl(photoIndex) {
      const photo = this.selectedPhotos[photoIndex]
      if (!photo) return
      return URL.createObjectURL(photo)
    },
    showPhoto(url) {
      this.$refs.photoModal.openModal(url)
    },
    getRealtyPhotoPromise(file) {
      return new Promise((resolve, reject) => {
        let reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onloadend = () => {
          let realtyPhoto = {}
          realtyPhoto['filename'] = file.name.trim()
          realtyPhoto['photo_data'] = reader.result
            .replace('data:', '')
            .replace(/^.+,/, '')
          resolve(realtyPhoto)
        }
        reader.onerror = (error) => reject(error)
      })
    },
    resetPhotos() {
      this.selectedPhotos = []
      this.photosToRemove = []
    },
    async updateSelectedPhotos() {
      this.uploading = true
      this.selectedPhotos = Array.from(this.$refs.photoInputUpload.files)
      await this.preparePhotos()
      let requestBody = {
        realtyId: this.realtyId,
        real_estate_id: this.realEstateId,
        realtyPhotos: this.preparedToUploadPhotos
      }
      await this.addRealtyPhotos(requestBody)
      await this.fetchRealtyPhotos(this.realtyId)
      this.resetPhotos()
      this.uploading = false
    },
    async deleteSelectedPhotos() {
      this.uploading = true
      let requestBody = {
        realtyId: this.realtyId,
        real_estate_id: this.realEstateId,
        realtyPhotosId: this.photosToRemove
      }
      await this.deleteRealtyPhotos(requestBody)
      await this.fetchRealtyPhotos(this.realtyId)
      this.resetPhotos()
      this.uploading = false
    },
    async preparePhotos() {
      let realtyPhotoPromises = []
      for (let index in this.selectedPhotos) {
        realtyPhotoPromises.push(
          this.getRealtyPhotoPromise(this.selectedPhotos[index])
        )
      }
      await Promise.all(realtyPhotoPromises).then((photos) => {
        this.preparedToUploadPhotos = photos
      })
    },
    selectAllPhotos() {
      if (this.photosToRemove.length !== this.realtyPhotos.length) {
        this.photosToRemove = this.realtyPhotos.map(photo => photo.id)
      } else {
        this.photosToRemove = []
      }
    },
    handleRemoveSelection() {
      this.onRemoveSelection = !this.onRemoveSelection
      this.photosToRemove = []
    }
  }
}
</script>

<style scoped>
  .outlined {
    border: 3px solid #009DD9;
    cursor: pointer !important;
  }
  .pointer {
    cursor: pointer !important;
  }
</style>
